import { type PersistStorage, persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";
import { toast } from "@/components/ui/sonner";
import type { CartUpdateAction } from "@/features/cart/types";
import type { Cart } from "@/features/cart/types/cart.type";
import type { OrderInvoice } from "@/features/cart/types/invoice-data.type";
import type { ContactInfoReturnType } from "@/features/checkout/forms";
import type {
  DietCatalog,
  DietCatalogCateringType,
} from "@/features/diets/types";
import {
  getObfuscatedLocalStorage,
  setObfuscatedLocalStorage,
} from "@/lib/storage";

type CartState = Cart & {
  // cartId is taken from the API - it's a unique identifier for the cart
  // It's used to identify the cart in the backend
  cartId?: string;

  // if the user created an order, we should store it for updating the cart
  currentOrderId?: string;

  // invoices
  invoiceData: OrderInvoice | null;
  invoiceActive: boolean;
  invoiceUseSameAddress?: boolean;

  // address error
  addressNotSupported?: boolean;

  // delivery costs
  deliveryCosts?: {
    itemId: string;
    dietId: string;
    cost: DietCatalogCateringType["deliveryCosts"];
  }[];

  // multidiet id
  multidietId?: string;
  ifFetching?: boolean;

  // order processing state
  isProcessingOrder: boolean;

  // global loading state
  isLoading: boolean;
};

type CartActions = {
  // cartId
  setCartId: (cartId: string) => void;
  setCart: (cart: Cart) => void;
  resetCart: () => void;

  // update cart
  updateCart: (
    cartId: string,
    cart: Cart,
    action: CartUpdateAction,
    t?: (key: string) => string,
    multidietId?: string,
  ) => Cart;

  // as id1,id2,id3
  getDietIds: () => string;

  setOrderId: (orderId: string) => void;
  getAddress: () => Partial<ContactInfoReturnType> | undefined;
  getInvoiceData: () => OrderInvoice | null;
  setInvoiceData: (invoiceData: OrderInvoice) => void;
  setInvoiceUseSameAddress: (useSameAddress: boolean) => void;

  // address error
  setAddressNotSupported: (value: boolean) => void;

  addDeliveryCost: (
    itemId: string,
    dietId: string,
    cost: DietCatalogCateringType["deliveryCosts"],
  ) => void;

  removeDeliveryCost: (itemId: string) => void;
  clearDeliveryCost: () => void;
  getDeliveryCost: () => DietCatalogCateringType["deliveryCosts"] | null;
  getDeliveryDietCost: (
    dietId: string,
  ) => DietCatalogCateringType["deliveryCosts"] | null;
  setDeliveryCosts: (diets: DietCatalog[]) => void;

  setMultidietId: (multidietId: string) => void;
  setIfFetching: (ifFetching: boolean) => void;

  // order processing
  setIsProcessingOrder: (value: boolean) => void;

  // global loading state
  setIsLoading: (value: boolean) => void;
};

type MultidietCartStore = CartState & CartActions;

const defaultValues: CartState = {
  cartId: undefined,
  currentOrderId: undefined,
  items: [],
  pricing: {
    final: {
      gross: 0,
      net: 0,
      tax: 0,
    },
    before: {
      gross: 0,
      net: 0,
      tax: 0,
    },
    discounts: [],
    delivery: {
      gross: 0,
      net: 0,
      tax: 0,
    },
  },
  discountCode: null,
  invoiceData: null,
  invoiceActive: false,
  agreements: {
    cateringPolicy: false,
    terms: false,
    marketing: false,
    privacy: false,
  },
  multidietId: undefined,
  isProcessingOrder: false,
  isLoading: false,
};

const storage: PersistStorage<CartState> = {
  getItem: (name: string) => {
    return getObfuscatedLocalStorage(name);
  },
  setItem: (name: string, value: unknown) => {
    setObfuscatedLocalStorage(name, value);
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

const createMultidietCartStore = ({ storageKey }: { storageKey: string }) =>
  createStore<MultidietCartStore>()(
    persist(
      (set, get) => ({
        // default state
        ...defaultValues,

        // cartId actions
        setCartId: (cartId: string) => set({ cartId }),
        setCart: (cart: Cart) => {
          return set(cart);
        },
        resetCart: () => set(defaultValues),

        // update cart actions
        updateCart: (cartId, cart, action, t, multidietId) => {
          set({ ...cart, cartId, multidietId });

          const translate = (message: string) => (t ? t(message) : message);

          // show toast message based on the action
          switch (action) {
            case "add":
              toast.success(translate("cart.added"));
              break;
            case "remove":
              toast.info(translate("cart.removed"));
              break;
            case "clear":
              toast.info(translate("cart.cleared"));
              break;
            case "update":
              toast.success(translate("cart.updated"));
              break;
            case "addDiscount":
              toast.success(translate("cart.addedDiscount"));
              break;
            case "addWrongDiscount":
              toast.error(translate("cart.addedWrongDiscount"));
              break;
            case "removeDiscount":
              toast.info(translate("cart.removedDiscount"));
              break;
            default:
              break;
          }
          get().setIfFetching(false);
          return get();
        },

        getDietIds: () => {
          return get()
            .items.map((item) => item.configurationItem.dietId)
            .join(",");
        },

        setOrderId: (orderId: string) => set({ currentOrderId: orderId }),

        getAddress: () => {
          const items = get().items;
          if (items.length > 0) {
            const item = items[0]?.configurationItem;
            if (item) {
              const contactStore: Partial<ContactInfoReturnType> = {
                address: item.address,
                email: item.contact?.email,
                phone: item.contact?.phoneNumber,
                firstName: item.contact?.firstName,
                lastName: item.contact?.lastName,
              };
              return contactStore;
            }
          }
        },

        getInvoiceData: () => {
          if (get().invoiceActive && get().invoiceData) {
            const data = get().invoiceData!;
            return {
              active: true,
              companyName: data.companyName,
              vatNumber: data.vatNumber,
              postCode: data.postCode,
              city: data.city,
              street: data.street,
              buildNumber: data.buildNumber,
              flatNumber: data.flatNumber,
            };
          }
          return null;
        },

        setInvoiceData: (invoiceData: OrderInvoice | null) => {
          set({ invoiceData });
        },

        setInvoiceUseSameAddress: (useSameAddress: boolean) =>
          set({ invoiceUseSameAddress: useSameAddress }),

        setAddressNotSupported: (value: boolean) =>
          set({ addressNotSupported: value }),

        addDeliveryCost: (itemId, dietId, cost) => {
          set((state) => ({
            deliveryCosts: [
              ...(state.deliveryCosts || []),
              { itemId, dietId, cost },
            ],
          }));
        },

        removeDeliveryCost: (itemId) => {
          set((state) => ({
            deliveryCosts: (state.deliveryCosts || []).filter(
              (item) => item.itemId !== itemId,
            ),
          }));
        },

        clearDeliveryCost: () => {
          set({ deliveryCosts: [] });
        },

        getDeliveryDietCost: (dietId: string) => {
          return (
            get().deliveryCosts?.find((cost) => cost.dietId === dietId)?.cost ||
            null
          );
        },

        getDeliveryCost: () => {
          if (get().items.length === 0) {
            return null;
          }
          const costs = get().deliveryCosts?.map((cost) => cost.cost);
          if (!costs) {
            return null;
          }

          // if there are multiple delivery costs, find the lowest price and return { type: 'from', price: lowestPrice }
          if (
            costs.length > 1 &&
            costs.some(
              (val) => JSON.stringify(val) !== JSON.stringify(costs[0]),
            )
          ) {
            // Extract prices, filter out null values
            const prices = costs
              .map((cost) => cost.price)
              .filter((price): price is number => price !== null);

            // If we have valid prices, find the minimum
            if (prices.length > 0) {
              const lowestPrice = Math.min(...prices);
              return { type: "from", price: lowestPrice };
            }

            // If all prices are null, fallback to original behavior
            return { type: "multiple", price: null };
          }

          return null;
        },

        // check delivery cost integrity
        setDeliveryCosts: (diets: DietCatalog[]) => {
          get().clearDeliveryCost();
          const deliveryCosts = get().deliveryCosts || [];

          // check if all items are in the delivery costs
          const items = get().items;

          // if there are no items, return
          if (!items.length) return;

          const missingItems = items.filter(
            (item) =>
              !deliveryCosts.some(
                (cost) => cost.itemId === item.configurationItem.id,
              ),
          );

          // if there are no missing items, return
          if (!missingItems.length) return;

          const missingCosts = missingItems.map((item) => {
            const diet = diets.find(
              (d) => d.id === item.configurationItem.dietId,
            );
            if (!(diet && item.configurationItem.id)) return null;
            return {
              itemId: item.configurationItem.id,
              dietId: diet.id,
              cost: diet.catering.deliveryCosts,
            };
          });

          for (const diet of missingCosts) {
            if (!diet) return;
            get().addDeliveryCost(diet.itemId, diet.dietId, diet.cost);
          }
        },

        setMultidietId: (multidietId: string) => set({ multidietId }),
        setIfFetching: (ifFetching: boolean) => set({ ifFetching }),
        setIsProcessingOrder: (value: boolean) =>
          set({ isProcessingOrder: value }),
        setIsLoading: (value: boolean) => set({ isLoading: value }),
      }),
      {
        name: storageKey,
        storage,
      },
    ),
  );

export { createMultidietCartStore, type MultidietCartStore };
